import { RawAlarmsFilterComponent, RawAlarmsFilterOutput } from '../../../core/components/table/filter-panel/filters/raw-alarms-filter/raw-alarms-filter.component';
import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { IsNewDirective } from '../../../../directives/is-new.directive';
import { DatepickerComponent } from '../../../core/components/datepicker/datepicker.component';
import { PaginationComponent } from '../../../core/components/pagination/pagination.component';
import { SearchComponent } from '../../../core/components/search/search.component';
import { SortComponent } from '../../../core/components/sort/sort.component';
import { EventInformationComponent } from '../../raw-alarm-information/raw-alarm-information.component';
import { Client, PageInfo, QueryAlarmDataOut, QueryEventAlarmDataIn, SortingType } from '../../../../api/api-reference';
import { getAlertColor } from '../../alert-colors';
import { ToastrService } from 'ngx-toastr';
import { ClipboardService } from '../../../../services/clipboard.service';
import { DateRangeInput } from '../../../core/models/date-range-input';
import { processDateRange } from '../../../../helper/date-utils';

@Component({
  selector: 'app-raw-alarms',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    SortComponent,
    SearchComponent,
    DatepickerComponent,
    PaginationComponent,
    RawAlarmsFilterComponent,
    EventInformationComponent,
    FormsModule,
    IsNewDirective,
  ],
  templateUrl: './raw-alarms.component.html',
  styleUrl: './raw-alarms.component.scss'
})
export class RawAlarmsComponent {
  public pageSize: number = 10;
  public pageNumber: number = 1;
  public currentPage: number = 1;
  public numberOfAlarms: number = 0;
  public lastPageSize: number = 1;
  readonly dayInMs = 24 * 60 * 60 * 1000;
  isDatepickerOpen = false;
  selectedFilters: string[] = [];
  filtersApplied = 0;

  isModalOpen = false;
  public selectedEvent: QueryAlarmDataOut | undefined;
  getAlertColor = getAlertColor;

  activeTab: string = 'all-events';
  data: QueryAlarmDataOut[] | undefined = [];
  requestData = new QueryEventAlarmDataIn({
    pageInfo: new PageInfo({ pageSize: this.pageSize, page: this.pageNumber }),
    search: undefined,
    startDate: undefined,
    endDate: undefined,
    sorting: SortingType.Descending,
    assetTypes: [],
  });

  onDataReceived(event: RawAlarmsFilterOutput) {
    this.selectedFilters = event.selectedAssetTypes;
    this.requestData.assetTypes = event.selectedAssetTypes;
    this.reloadTable();
  }

  setActiveTab(tab: string): void {
    this.activeTab = tab;
    if (tab === 'all-events') {
      this.requestData.startDate = undefined;
      this.requestData.endDate = undefined;
    } else if (tab === 'recently-flagged') {
      const startOfDay = new Date();
      startOfDay.setHours(1, 0, 0, 0);

      const endOfDay = new Date();
      endOfDay.setHours(24, 59, 59, 0);

      this.requestData.startDate = startOfDay;
      this.requestData.endDate = endOfDay;
    }
    this.reloadTable(1);
    this.requestData.endDate!.setDate(this.requestData.endDate!.getDate() - 1);
  }

  constructor(
    private toastr: ToastrService,
    private client: Client,
    public clipboardService: ClipboardService
  ) {
    this.reloadTable(1);
  }

  onSortOption(selectedSort?: SortingType) {
    if (selectedSort) this.requestData.sorting = selectedSort;
    this.reloadTable();
  }

  searchChange(searchText: string) {
    this.requestData.search = searchText;
    this.reloadTable(1);
  }

  pageChange(value: number) {
    this.currentPage = value;
    this.reloadTable(this.currentPage);
  }

  reloadTable(page?: number) {
    this.pageNumber = page || 0;
    if (page) this.requestData!.pageInfo!.page = this.pageNumber;

    this.selectedEvent = undefined;
    this.isModalOpen = false;

    this.client.queryRealtimeAlarm(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data;
        this.numberOfAlarms = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfAlarms / this.pageSize);
        this.filtersApplied = this.requestData.assetTypes?.length || 0;
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  handleRowClick(event: string, command: string) {
    if (!this.isModalOpen) {
      this.selectedEvent = new QueryAlarmDataOut();
      this.selectedEvent.eventId = event;
      this.selectedEvent.commandId = command;
      this.isModalOpen = true;
    }
  }

  toggleDatepicker() {
    this.isDatepickerOpen = !this.isDatepickerOpen;
  }

  toggleModal() {
    this.isModalOpen = !this.isModalOpen;
  }

  selectedDate: string | null = '';
  onDateRangeChanged(input: DateRangeInput): void {
    const result = processDateRange(input);

    this.requestData.startDate = result.startDate;
    this.requestData.endDate = result.endDate;
    this.selectedDate = result.selectedDate;
    this.filterDatesInTable();
  }

  clearDates(): void {
    this.requestData.startDate = undefined;
    this.requestData.endDate = undefined;
    this.reloadTable();
  }

  filterDatesInTable() {
    this.client.queryRealtimeAlarm(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data;
        this.numberOfAlarms = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfAlarms / this.pageSize);
        this.filtersApplied = this.requestData.assetTypes?.length || 0;
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }
}
